import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  Select, MenuItem, FormControl, InputLabel, TextField,
  Chip, CircularProgress,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import PaymentIcon from "@mui/icons-material/Payment";
import { useSnackbar } from "notistack";
import { currencySymbol, regionOf } from "../../../utils/region";
import {
  textPrimary, textSecondary, font, accent, success, navy, surfaceRaised, border,
  primaryButtonSx, secondaryButtonSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import {
  get_school_tutors_config_api, update_school_tutors_config_api,
  create_school_connect_onboarding_link_api, get_school_connect_status_api,
} from "../../../DAL/SchoolPortal/Tutors";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "9px",
    color: textPrimary,
    "& fieldset": { borderColor: border },
    "&:hover fieldset": { borderColor: "rgba(26,26,26,0.3)" },
    "&.Mui-focused fieldset": { borderColor: accent, borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: textPrimary },
  "& .MuiInputLabel-root": { color: "#6b6b6b", fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: accent },
};

const sectionSx = {
  backgroundColor: surfaceRaised,
  border: `1px solid ${border}`,
  borderRadius: "12px",
  p: 2.5,
  mb: 2.5,
};

const badgeSx = (color) => ({
  width: 32, height: 32, borderRadius: "9px", flexShrink: 0,
  backgroundColor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center",
});

// Completing setup in place, right where the Owner/Manager hit the wall,
// instead of sending them off to a separate Settings page. Deliberately not
// dismissible by backdrop click / Escape: closing this without saving just
// means they're back at the same "not set up" wall on their next visit, so
// it stays open until they explicitly cancel or save.
export default function TutorsSetupModal({ open, onClose, onComplete }) {
  const { enqueueSnackbar } = useSnackbar();
  const [config, setConfig] = useState(null);
  const schoolRegion = regionOf(config);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([get_school_tutors_config_api(), get_school_connect_status_api()]).then(([configRes, connectRes]) => {
      if (configRes.code === 200) setConfig(configRes.data);
      if (connectRes.code === 200) setConnectStatus(connectRes.data);
      setLoading(false);
    });
  }, [open]);

  const saveConfig = async (updates) => {
    setSaving(true);
    const res = await update_school_tutors_config_api(updates);
    if (res.code === 200) {
      setConfig(res.data);
    } else {
      enqueueSnackbar(res.message || "Couldn't save Tutors settings", { variant: "error" });
    }
    setSaving(false);
    return res.code === 200;
  };

  const handleSchoolConnectClick = async () => {
    // This modal can be opened from anywhere, so send them back to whichever
    // page they had open rather than to a fixed one.
    const res = await create_school_connect_onboarding_link_api(
      window.location.pathname + window.location.search
    );
    if (res.code === 200 && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      enqueueSnackbar(res.message || "Couldn't start Stripe onboarding", { variant: "error" });
    }
  };

  const handleFinish = async () => {
    if (!config?.setupComplete) {
      const ok = await saveConfig({});
      if (!ok) return;
    }
    onComplete();
  };

  const marketplaceOn = Boolean(config?.allowOpenMarketplace);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#ffffff", borderRadius: "16px", border: `1px solid ${border}` } }}
    >
      <DialogTitle sx={{ fontFamily: font, fontWeight: 800, color: textPrimary, fontSize: "19px" }}>
        Set up Tutors for your school
      </DialogTitle>
      <DialogContent>
        {loading || !config ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress sx={{ color: accent }} /></Box>
        ) : (
          <>
            <Typography sx={{ fontSize: "13.5px", color: "#5a5550", fontFamily: font, mb: 2.5 }}>
              Choose these once and your instructors and students can get started — you can change any of it later in Settings.
            </Typography>

            {/* Marketplace */}
            <Box sx={sectionSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
                <Box sx={badgeSx(accent)}><PublicIcon sx={{ color: accent, fontSize: 18 }} /></Box>
                <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "14.5px", color: textPrimary }}>Open marketplace</Typography>
              </Box>

              <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#4a4540", lineHeight: 1.6, mb: 1.5 }}>
                By default, only your own students can book lessons with your own instructors — nobody outside your school
                sees you, and vice versa. Turning this on opens a two-way door: your instructors become bookable by students
                at other participating schools <em>and</em> by independent XAir students with no school of their own —
                giving your school worldwide reach — while your own students can book instructors elsewhere too.
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                <Box
                  onClick={() => !saving && saveConfig({ allowOpenMarketplace: false })}
                  sx={{
                    p: 1.5, borderRadius: "9px", backgroundColor: "#ffffff", cursor: saving ? "default" : "pointer",
                    border: `1.5px solid ${!marketplaceOn ? accent : border}`,
                    boxShadow: !marketplaceOn ? `0 0 0 1px ${accent}` : "none",
                  }}
                >
                  <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12px", color: !marketplaceOn ? accent : textPrimary, mb: 0.5 }}>
                    {!marketplaceOn ? "✓ " : ""}Off (default)
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#5a5550", lineHeight: 1.5 }}>
                    In-house only — your own students booking your own instructors. Your platform fee applies as per your contract.
                  </Typography>
                </Box>
                <Box
                  onClick={() => !saving && saveConfig({ allowOpenMarketplace: true })}
                  sx={{
                    p: 1.5, borderRadius: "9px", backgroundColor: "#ffffff", cursor: saving ? "default" : "pointer",
                    border: `1.5px solid ${marketplaceOn ? accent : border}`,
                    boxShadow: marketplaceOn ? `0 0 0 1px ${accent}` : "none",
                  }}
                >
                  <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "12px", color: marketplaceOn ? accent : textPrimary, mb: 0.5 }}>
                    {marketplaceOn ? "✓ " : ""}On
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "12px", color: "#5a5550", lineHeight: 1.5 }}>
                    Also opens cross-school bookings, either direction, plus every independent XAir student worldwide —
                    no school required on their end. Your platform fee applies the same either way.
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Payouts & rates */}
            <Box sx={sectionSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
                <Box sx={badgeSx(navy)}><PaymentIcon sx={{ color: navy, fontSize: 18 }} /></Box>
                <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "14.5px", color: textPrimary }}>Payouts & rates</Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Payout goes to</InputLabel>
                  <Select value={config.payoutMode || "instructor"} label="Payout goes to" onChange={(e) => saveConfig({ payoutMode: e.target.value })} disabled={saving}>
                    <MenuItem value="instructor">Directly to each instructor</MenuItem>
                    <MenuItem value="school">Pooled to the school's own account</MenuItem>
                    <MenuItem value="split">Split between school and instructor</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Instructor rates</InputLabel>
                  <Select value={config.rateMode || "org_standard"} label="Instructor rates" onChange={(e) => saveConfig({ rateMode: e.target.value })} disabled={saving}>
                    <MenuItem value="org_standard">Everyone charges the school rate</MenuItem>
                    <MenuItem value="admin_set">You set each instructor's rate</MenuItem>
                    <MenuItem value="instructor_set">Instructors set their own rate</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                label={`School default hourly rate (${currencySymbol(schoolRegion)})`}
                type="number"
                sx={inputSx}
                value={config.defaultHourlyRate ?? 50}
                onChange={(e) => setConfig((prev) => ({ ...prev, defaultHourlyRate: e.target.value }))}
                onBlur={(e) => saveConfig({ defaultHourlyRate: parseFloat(e.target.value) || 0 })}
              />

              {config.payoutMode === "school" && (
                <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${border}` }}>
                  <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: textPrimary, mb: 1 }}>School payout account</Typography>
                  {connectStatus?.onboardingComplete ? (
                    <Chip label="Connected" sx={{ backgroundColor: `${success}1a`, color: success, fontWeight: 700, fontFamily: font }} />
                  ) : (
                    <Button onClick={handleSchoolConnectClick} sx={secondaryButtonSx}>Set up school payouts</Button>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={onClose} sx={{ color: textSecondary, textTransform: "none", fontFamily: font }}>
          I'll finish this later
        </Button>
        <Button onClick={handleFinish} disabled={loading || saving} sx={primaryButtonSx}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
